import { AudioNote } from '@highlight/common'
import { GlobalEvents } from '@highlight/ipc'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { useQueryClient } from '@tanstack/react-query'
import { AUDIO_NOTES_QUERY_KEY } from '@renderer/hooks/audio-notes'
import { useCallback, useMemo, useState } from 'react'
import { useSelectedAudioNote } from '@renderer/hooks/useSelectedAudioNote'
import { useSetSelectedAudioNoteId } from './side-panel'

export function useAudioNotesStore() {
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const { data: selectedAudioNote } = useSelectedAudioNote()
  const setSelectedAudioNoteId = useSetSelectedAudioNoteId()
  const queryClient = useQueryClient()

  const invalidateAudioNotes = useCallback(async () => {
    // Invalidate all queries that start with AUDIO_NOTES_QUERY_KEY
    // This will match both ['audio-notes'] and ['audio-notes', true/false]
    await queryClient.invalidateQueries({
      queryKey: [AUDIO_NOTES_QUERY_KEY],
      exact: false,
    })
  }, [queryClient])

  const updateAudioNote = useCallback(
    async (audioNoteUpdates: Partial<Omit<AudioNote, 'id'>> & { id: AudioNote['id'] }) => {
      setIsUpdateLoading(true)
      const { id, ...updates } = audioNoteUpdates

      try {
        if (selectedAudioNote?.id === id) {
          setSelectedAudioNoteId(id)
        }

        await AudioNoteIpcRenderer.updateAudioNote({ id, updates })

        await invalidateAudioNotes()
        await queryClient.invalidateQueries({ queryKey: ['audio-note', id] })
        // Cross-renderer: the overlay's history feed shows this note's title.
        GlobalEvents.emit('audio-notes:changed')
        setIsUpdateLoading(false)
        console.log('Audio note updated successfully:', id)
      } catch (error) {
        console.error('Error updating conversation:', error)
        setIsUpdateLoading(false)
      }
    },
    [setIsUpdateLoading, invalidateAudioNotes, setSelectedAudioNoteId, selectedAudioNote],
  )

  return useMemo(() => {
    return {
      updateAudioNote,
      updateAudioNoteIsLoading: isUpdateLoading,
      invalidateAudioNotes,
    }
  }, [updateAudioNote, isUpdateLoading, invalidateAudioNotes])
}
